import { add } from 'date-fns'
import { OLDEST_ALLOWED_AGE, YOUNGEST_ALLOWED_AGE } from '@/lib/constants'
import * as React from 'react'
import { useCallback } from 'react'
import { WebConfig } from '@/lib/config'
import { useFormContext } from 'react-hook-form'
import RHFTextInput from '@/shared/forms/RHFTextInput'
import RHFDropdown from '@/shared/forms/RHFDropdown'
import RHFDatePicker from '@/shared/forms/RHFDatePicker'
import RHFGooglePlacesAutocompleteInput from '@/shared/forms/RHFGooglePlacesAutocompleteInput'
import RHFToggle from '@/shared/forms/RHFToggle'
import { DetailedAddress } from '@/lib/types'

export type PersonalInformationFormFieldsProps = {
  disabledFields?: Set<string>
  verifiedFields?: Set<string>
}

export const AU_STATES = ['ACT', 'NSW', 'NT', 'SA', 'TAS', 'VIC', 'WA', 'QLD']

export default function RHFProfileFormInputCollection({
  disabledFields = new Set(),
  verifiedFields = new Set(),
}: PersonalInformationFormFieldsProps) {
  const { setValue, trigger } = useFormContext()

  const onPickAddress = useCallback(
    async (address: DetailedAddress) => {
      setValue('state', address.state)
      setValue('suburb', address.suburb)
      setValue('postcode', address.postcode)
      setValue('formattedAddress', address.formattedAddress)
      await trigger(['state', 'suburb', 'postcode', 'formattedAddress'])
    },
    [setValue, trigger],
  )

  return (
    <>
      <RHFTextInput
        size="1/2"
        label="First Name"
        autoComplete="given-name"
        name="firstName"
        disabled={disabledFields?.has('firstName')}
      />
      <RHFTextInput
        size="1/2"
        label="Last Name"
        autoComplete="family-name"
        name="lastName"
        disabled={disabledFields?.has('lastName')}
      />
      <RHFDropdown
        size="full"
        label="Gender"
        autoComplete="gender"
        name="gender"
        options={[
          { id: 'M', label: 'Male' },
          { id: 'F', label: 'Female' },
        ]}
        labelWhenNotSet="Select Gender"
        disabled={disabledFields?.has('gender')}
      />

      <RHFTextInput
        size="1/2"
        label="Mobile"
        autoComplete="mobile"
        name="mobile"
        placeholder="0468895000"
        inputMode="numeric"
        disabled={disabledFields?.has('mobile')}
        verified={verifiedFields?.has('mobile')}
      />
      <RHFTextInput
        size="1/2"
        label="Email"
        autoComplete="email"
        name="email"
        disabled={disabledFields?.has('email')}
        verified={verifiedFields?.has('email')}
      />

      <RHFDatePicker
        label="Date of birth"
        size="full"
        name="dob"
        variant="month-and-year"
        fromDate={add(new Date(), { years: -OLDEST_ALLOWED_AGE })}
        toDate={add(new Date(), { years: -YOUNGEST_ALLOWED_AGE })}
        disabled={disabledFields?.has('dob')}
      />

      <RHFGooglePlacesAutocompleteInput
        size="full"
        label="Address"
        autoComplete="street-address"
        name="formattedAddress"
        onPickAddress={onPickAddress}
        disabled={disabledFields?.has('formattedAddress')}
      />
      <RHFTextInput
        size="1/3"
        label="Suburb"
        autoComplete="address-level2"
        name="suburb"
        disabled={disabledFields?.has('suburb')}
      />
      <RHFDropdown
        size="1/3"
        label="State"
        autoComplete="address-level1"
        name="state"
        labelWhenNotSet="Pick a state"
        options={AU_STATES.map((state) => ({ id: state, label: state }))}
        disabled={disabledFields?.has('state')}
      />
      <RHFTextInput
        size="1/3"
        label="Postcode"
        autoComplete="postal-code"
        name="postcode"
        inputMode="numeric"
        disabled={disabledFields?.has('postcode')}
      />

      {WebConfig.prescriptionConformanceMode && (
        <>
          <RHFTextInput
            size="full"
            label="Concession Number"
            name="concessionNumber"
            disabled={disabledFields?.has('concessionNumber')}
          />
          <RHFTextInput
            size="full"
            label="Entitlement Number"
            name="entitlementNumber"
            disabled={disabledFields?.has('entitlementNumber')}
          />
          <RHFToggle
            label="I identify myself as being of Aboriginal and/or Torres Strait Islander origin"
            size="full"
            name="isAbOrTorIslander"
          />
        </>
      )}
    </>
  )
}
